// Ctrl+K / Cmd+K opens the app directory, but never while the member is
// typing: inputs, textareas, selects, contenteditable regions, and any
// focused control inside an open dialog keep the keystroke for themselves.

type ShortcutEvent = Pick<KeyboardEvent, 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey' | 'key'>;

function asElement(node: unknown): HTMLElement | null {
  if (!node || typeof node !== 'object') return null;
  const el = node as HTMLElement;
  return typeof el.tagName === 'string' ? el : null;
}

export function isEditableTarget(node: unknown): boolean {
  const el = asElement(node);
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (el.isContentEditable) return true;
  return typeof el.closest === 'function'
    && el.closest('[contenteditable]:not([contenteditable="false"])') != null;
}

function insideOpenDialog(node: unknown): boolean {
  const el = asElement(node);
  return Boolean(el && typeof el.closest === 'function'
    && el.closest('[role="dialog"], [role="alertdialog"], dialog[open]'));
}

export function isDirectoryShortcut(event: ShortcutEvent): boolean {
  return (event.ctrlKey || event.metaKey)
    && !event.altKey
    && !event.shiftKey
    && typeof event.key === 'string'
    && event.key.toLowerCase() === 'k';
}

/** True when this keydown should open the app directory. */
export function shouldOpenDirectory(
  event: ShortcutEvent & Pick<KeyboardEvent, 'target'>,
  activeElement: Element | null = typeof document !== 'undefined' ? document.activeElement : null,
): boolean {
  if (!isDirectoryShortcut(event)) return false;
  for (const node of [event.target, activeElement]) {
    if (isEditableTarget(node) || insideOpenDialog(node)) return false;
  }
  return true;
}
